import { SolutionType } from './application/solution-type';
import { AcquiringChannel } from './application/acquiring-channel';
import { BankDetails } from './application/bank-details';
import { Business } from './application/business';
import { Contact } from './application/contact';
import { DirectorDetails } from './application/director-details';
import { OnboardingDocument } from './application/onboarding-document';
import { PaymentStatus, PaymentTypes } from './application/payment';
import { Delivery, Recipient, Solution } from './application/opportunity';
import { Application, ApplicationStep } from './application/application';
import { Address, AddressWithMeta } from './application/address';
import { BankAccountValidationStatus } from './application/credit-vetting';
import { Catalogue, CatalogueItem } from './catalogue';
import { CardFeeDetailsV2DTO } from './fees/dto';
import { CardMachine } from 'src/features/common/types/application/card-machine';
import { PaymentMethodCode } from './salesforce';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import { Collection } from 'src/features/common/types/application/collection';
import { CartItem } from './application/cart';
import { PriceType } from '@vfs-digital-channels/ns-utils';

export enum ApplicationActionType {
  START_NEW = 'START_NEW',
  RESTART = 'RESTART',
  START_TAP_ON_PHONE_JOURNEY = 'START_TAP_ON_PHONE_JOURNEY',
  START_ONBOARDING_JOURNEY = 'START_ONBOARDING_JOURNEY',
  NAVIGATE_TO_NEXT_STEP = 'NAVIGATE_TO_NEXT_STEP',
  NAVIGATE_TO_PREVIOUS_STEP = 'NAVIGATE_TO_PREVIOUS_STEP',
  UPDATE_SOLUTION_TYPE = 'UPDATE_SOLUTION_TYPE',
  UPDATE_BASKET_QUANTITY = 'UPDATE_BASKET_QUANTITY',
  REMOVE_BASKET_QUANTITY = 'REMOVE_BASKET_QUANTITY',
  UPDATE_CART_QUANTITY = 'UPDATE_CART_QUANTITY',
  REMOVE_CART_QUANTITY = 'REMOVE_CART_QUANTITY',
  UPDATE_CONTACT_DETAILS = 'UPDATE_CONTACT_DETAILS',
  ADD_ADDRESS = 'ADD_ADDRESS',
  ADD_DELIVERY_ADDRESS = 'ADD_DELIVERY_ADDRESS',
  UPDATE_DELIVERY_ADDRESS = 'UPDATE_DELIVERY_ADDRESS',
  UPDATE_REFERENCE_NUMBER = 'UPDATE_REFERENCE_NUMBER',
  UPDATE_PAYMENT_TYPE = 'UPDATE_PAYMENT_TYPE',
  UPDATE_PAYMENT_STATUS = 'UPDATE_PAYMENT_STATUS',
  UPDATE_DELIVERIES = 'UPDATE_DELIVERIES',
  MULTIPLE_ACTIONS = 'MULTIPLE_ACTIONS',
  UPDATE_DIRECTOR_DETAILS = 'UPDATE_DIRECTOR_DETAILS',
  UPDATE_BUSINESS_DETAILS = 'UPDATE_BUSINESS_DETAILS',
  UDPATE_RESIDENTIAL_ADDRESS = 'UPDATE_RESIDENTIAL_ADDRESS',
  UPDATE_DOCUMENTS = 'UPDATE_DOCUMENTS',
  SET_WHOLE_APP = 'SET_WHOLE_APP',
  UPDATE_BANK_DETAILS = 'UPDATE_BANK_DETAILS',
  UPDATE_CREDIT_VETTING_STATUS = 'UPDATE_CREDIT_VETTING_STATUS',
  KILL_APPLICATION = 'KILL_APPLICATION',
  QUICK_EDIT = 'QUICK_EDIT',
  UPDATE_CARD_FEES = 'UPDATE_CARD_FEES',
  UPDATE_SALES_DETAILS = 'UPDATE_SALES_DETAILS',
  UPDATE_REMOTE_PAYMENT_STATUS = 'UPDATE_REMOTE_PAYMENT_STATUS',
  RESET_REMOTE_PAYMENT_STATUS = 'RESET_REMOTE_PAYMENT_STATUS',
  SET_GATEWAY_PAYMENT_STATUS = 'SET_GATEWAY_PAYMENT_STATUS',
  UPDATE_GATEWAY_PAYMENT_STATUS = 'UPDATE_GATEWAY_PAYMENT_STATUS',
  RESET_GATEWAY_PAYMENT_STATUS = 'RESET_GATEWAY_PAYMENT_STATUS',
  UPDATE_DEFAULT_ADDRESS_DELIVERY_OPTION = 'DELIVERY_OPTION',
  HIGHLIGHT_ADDRESS = 'HIGHLIGHT_ADDRESS',
  HIGHLIGHT_DELIVERY = 'HIGHLIGHT_DELIVERY',
  ATTACH_SERIAL_NUMBER = 'ATTACH_SERIAL_NUMBER',
  DEVICE_ACTIVATION = 'DEVICE_ACTIVATION',
  UPDATE_CARD_MACHINE = 'UPDATE_CARD_MACHINE',
  UPDATE_THIRD_PARTY_DEFAULT_ADDRESS_SOLUTIONS = 'UPDATE_THIRD_PARTY_DEFAULT_ADDRESS_SOLUTIONS',
  INIT_PAYMENT_METHOD_CODE = 'INIT_PAYMENT_METHOD_CODE',
  ADD_PAYMENT_METHOD_CODE = 'ADD_PAYMENT_METHOD_CODE',
  SET_SELECTABLE_PAYMENT_CODES = 'SET_SELECTABLE_PAYMENT_CODES',
  UPDATE_SELECTABLE_PAYMENT_CODES = 'UPDATE_SELECTABLE_PAYMENT_CODES',
  UPDATE_SELECTED_PAYMENT_METHOD = 'UPDATE_SELECTED_PAYMENT_METHOD',
  SHOW_DEVICE_ACTIVATION = 'SHOW_DEVICE_ACTIVATION',
  FEE_SLIDER = 'FEE_SLIDER',
  UPDATE_DELIVERY_OPTIONS = 'UPDATE_DELIVERY_OPTIONS',
  UPDATE_DEVICE_DELIVERIES = 'UPDATE_DEVICE_DELIVERIES',
  DELETE_ADDRESS = 'DELETE_ADDRESS',
  UPDATE_DEVICE_COLLECTIONS = 'UPDATE_DEVICE_COLLECTIONS',
  UPDATE_ADDRESS = 'UPDATE_ADDRESS',
  SET_DPO = 'SET_DPO',
  UPDATE_CART_PAYMENT_TYPE = 'UPDATE_CART_PAYMENT_TYPE',
  NORMALISE_ADDRESSES = 'NORMALISE_ADDRESSES',
  SET_MERCHANT_AGREEMENT = 'SET_MERCHANT_AGREEMENT',
  SET_SIGNED_MERCHANT_AGREEMENT = 'SET_SIGNED_MERCHANT_AGREEMENT',
  SET_MERCHANT_AGREEMENT_UPLOADED = 'SET_MERCHANT_AGREEMENT_UPLOADED',
  SET_CONTINUE_WITH_OTP_APP = 'SET_CONTINUE_WITH_OTP_APP',
  SET_TERMS_AND_CONDITIONS = 'SET_TERMS_AND_CONDITIONS',
}

export type StartOrderJourney = {
  type: ApplicationActionType.START_NEW;
  solutionType: SolutionType;
  creditVettingRequired: boolean;
  passportEnabled: boolean;
  brokerLogin: string;
  agentJourney: boolean;
  useStockOnHandDeliveryOption: boolean;
  highlightedAddress: string;
  agentId: string;
  remotePaymentsEnabled: boolean;
  dpoEnabled?: boolean;
  feeSliderPage?: boolean;
};

export type UpdateCartPaymentType = {
  type: ApplicationActionType.UPDATE_CART_PAYMENT_TYPE;
  cart: CartItem[];
};

export type SetMerchantAgreement = {
  type: ApplicationActionType.SET_MERCHANT_AGREEMENT;
  merchantAgreementScreenshot: string;
};

export type SetSignedMerchantAgreement = {
  type: ApplicationActionType.SET_SIGNED_MERCHANT_AGREEMENT;
  signedMerchantAgreement: string;
};

export type SetMerchantAgreementUploaded = {
  type: ApplicationActionType.SET_MERCHANT_AGREEMENT_UPLOADED;
  isMerchantAgreementUploaded: boolean;
};

export type SetTermsAndConditions = {
  type: ApplicationActionType.SET_TERMS_AND_CONDITIONS;
  termsAndConditions: boolean;
};

export type SetDPOJourney = {
  type: ApplicationActionType.SET_DPO;
  dpoEnabled: boolean;
};

export type AddAddress = {
  type: ApplicationActionType.ADD_ADDRESS;
  address: Address;
  recipient?: Recipient;
};

export type StartTapOnPhoneJourney = {
  type: ApplicationActionType.START_TAP_ON_PHONE_JOURNEY;
  creditVettingRequired: boolean;
  passportEnabled: boolean;
};

export type RestartOrderJourney = {
  type: ApplicationActionType.RESTART;
};

export type StartOnboardingJourney = {
  type: ApplicationActionType.START_ONBOARDING_JOURNEY;
};

export type NavigateToNextStep = {
  type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP;
};

// Updates the cart quantity for a specific device and acquiring channel
export type UpdateCartQuantity = {
  type: ApplicationActionType.UPDATE_CART_QUANTITY;
  productId: string;
  acquiringChannel: AcquiringChannel;
  quantity: number;
  catalogue: Catalogue;
  paymentMethodCode?: PaymentMethodCode | null;
  cartItemId: string;
};

// Removes all quantities for a specific device and acquiring channel
export type RemoveCartQuantity = {
  type: ApplicationActionType.REMOVE_CART_QUANTITY;
  productId: string;
  acquiringChannel: AcquiringChannel;
  catalogue: Catalogue;
  cartItemId: string;
};

export type AddDeliveryAddress = {
  type: ApplicationActionType.ADD_DELIVERY_ADDRESS;
  address: Address;
  products: { id: string; quantity: number; cartItemId: string; priceType: PriceType }[];
  isDefaultAddress?: boolean;
};

export type UpdateDeliveryAddress = {
  type: ApplicationActionType.UPDATE_DELIVERY_ADDRESS;
  address: Address;
  products: { id: string; quantity: number }[];
};

export type NavigateToPreviousStep = {
  type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP;
};

export type UpdateSolutionType = {
  type: ApplicationActionType.UPDATE_SOLUTION_TYPE;
  solutionType: SolutionType;
};

// Updates the basket quantity for a specific device and acquiring channel
export type UpdateBasketQuantity = {
  type: ApplicationActionType.UPDATE_BASKET_QUANTITY;
  productId: string;
  acquiringChannel: AcquiringChannel;
  quantity: number;
  catalogue: Catalogue;
};

// Removes all quantities for a specific device and acquiring channel
export type RemoveBasketQuantity = {
  type: ApplicationActionType.REMOVE_BASKET_QUANTITY;
  productId: string;
  acquiringChannel: AcquiringChannel;
  catalogue: Catalogue;
};

// Removes all quantities for a specific device and acquiring channel
export type UpdateContactDetails = {
  type: ApplicationActionType.UPDATE_CONTACT_DETAILS;
  contactDetails: Contact;
};

export type UpdateDeviceDeliveries = {
  type: ApplicationActionType.UPDATE_DEVICE_DELIVERIES;
  deliveries: AppDelivery[];
};

export type UpdateDeviceCollections = {
  type: ApplicationActionType.UPDATE_DEVICE_COLLECTIONS;
  collections: Collection[];
};

export type RemoveAddress = {
  type: ApplicationActionType.DELETE_ADDRESS;
  addressId: string;
};

export type UpdateAddress = {
  type: ApplicationActionType.UPDATE_ADDRESS;
  address: Address;
  recipient?: Recipient;
};

export type UpdateReferenceNumber = {
  type: ApplicationActionType.UPDATE_REFERENCE_NUMBER;
  referenceNumber?: string;
};

// Batches multiple action in a single action. Actions are executed starting from index zero
export type MultipleActions = {
  type: ApplicationActionType.MULTIPLE_ACTIONS;
  actions: ApplicationAction[];
};

export type UpdatePaymentType = {
  type: ApplicationActionType.UPDATE_PAYMENT_TYPE;
  paymentType?: PaymentTypes;
  onlinePaymentRequired: boolean;
};

export type UpdatePaymentStatus = {
  type: ApplicationActionType.UPDATE_PAYMENT_STATUS;
  paymentStatus?: PaymentStatus;
  orderNumber?: string;
};

export type UpdateDirectorDetails = {
  type: ApplicationActionType.UPDATE_DIRECTOR_DETAILS;
  directorDetails: DirectorDetails;
};

export type UpdateBusinessDetails = {
  type: ApplicationActionType.UPDATE_BUSINESS_DETAILS;
  businessDetails: Business;
};

export type UpdateResidentialAddress = {
  type: ApplicationActionType.UDPATE_RESIDENTIAL_ADDRESS;
  address: AddressWithMeta;
  isResidentialAddressSameAsBusinessAddress: boolean;
};

export type UpdateDocuments = {
  type: ApplicationActionType.UPDATE_DOCUMENTS;
  documents?: OnboardingDocument[];
};

export type UpdateDeliveries = {
  type: ApplicationActionType.UPDATE_DELIVERIES;
  deliveries: Delivery[];
};

export type SetWholeApp = {
  type: ApplicationActionType.SET_WHOLE_APP;
  application: Application;
  documents?: OnboardingDocument[];
  catalogue: Catalogue;
  dealerId?: string;
  remotePaymentsEnabled?: boolean;
};

// Assume that DPO is enabled, we don't pass the catalogue, since it's not needed.
//  Mainly used in Device Activation and 3PO Continue Application
export type SetContinueWithOTPApp = {
  type: ApplicationActionType.SET_CONTINUE_WITH_OTP_APP;
  application: Application;
  remotePaymentsEnabled?: boolean;
  dealerId?: string;
};

export type UpdateBankingDetails = {
  type: ApplicationActionType.UPDATE_BANK_DETAILS;
  bankDetails?: BankDetails;
};

export type UpdateCreditVettingStatus = {
  type: ApplicationActionType.UPDATE_CREDIT_VETTING_STATUS;
  bankAccountValidationStatus?: BankAccountValidationStatus;
  bankAccountValidationAttempts?: number;
};

export type KillApplication = {
  type: ApplicationActionType.KILL_APPLICATION;
};

export type QuickEditAction = {
  type: ApplicationActionType.QUICK_EDIT;
  steps: ApplicationStep[];
};

export type UpdateCardFees = {
  type: ApplicationActionType.UPDATE_CARD_FEES;
  cardFeesDTO: CardFeeDetailsV2DTO;
  products: CatalogueItem[];
};

export type UpdateSalesDetails = {
  type: ApplicationActionType.UPDATE_SALES_DETAILS;
  organisation: string;
  agent: string;
};

export type SetGatewayPaymentStatus = {
  type: ApplicationActionType.SET_GATEWAY_PAYMENT_STATUS;
  initiated: boolean;
  transactionId: string;
  numberOfTries?: number;
};

export type UpdateGatewayPaymentStatus = {
  type: ApplicationActionType.UPDATE_GATEWAY_PAYMENT_STATUS;
  transactionId: string;
  responseCode: string;
  responseMessage: string;
  isSuccessful: boolean;
};

export type ResetGatewayPaymentStatus = {
  type: ApplicationActionType.RESET_GATEWAY_PAYMENT_STATUS;
};

export type UpdateRemotePaymentStatus = {
  type: ApplicationActionType.UPDATE_REMOTE_PAYMENT_STATUS;
  initiated: boolean;
  transactionId: string;
  responseCode: string;
  responseMessage: string;
  orderNumber?: string;
  isNew?: boolean;
};

export type ResetRemotePaymentStatus = {
  type: ApplicationActionType.RESET_REMOTE_PAYMENT_STATUS;
};

export type HighlightAddress = {
  type: ApplicationActionType.HIGHLIGHT_ADDRESS;
  highlightedAddress: string;
};

export type HighlightDelivery = {
  type: ApplicationActionType.HIGHLIGHT_DELIVERY;
  deliveryId: string;
};

// Action will be used in Device Activations feature
export type AttachSerialNumber = {
  type: ApplicationActionType.ATTACH_SERIAL_NUMBER;
  serialNumber: string[];
  productId: string;
  friendlyTerminalName?: string[];
};

export type DeviceActivation = {
  type: ApplicationActionType.DEVICE_ACTIVATION;
  dpoEnabled?: boolean;
};

export type UpdateCardMachine = {
  type: ApplicationActionType.UPDATE_CARD_MACHINE;
  cardMachines: CardMachine[];
};

export type Update3rdPartyDefaultAddressSolutions = {
  type: ApplicationActionType.UPDATE_THIRD_PARTY_DEFAULT_ADDRESS_SOLUTIONS;
  thirdPartyDefaultAddressSolutions: Solution[];
};

export type FeesSlider = {
  type: ApplicationActionType.FEE_SLIDER;
};

// Sets up the initial payment_method_code on your details
export type InitPaymentMethodCode = {
  type: ApplicationActionType.INIT_PAYMENT_METHOD_CODE;
  productId: string;
  catalogue: Catalogue;
  acquiringChannel: AcquiringChannel;
  paymentMethodCode: PaymentMethodCode | null;
};

// Adds the selected/default payment_method_code on summary screen
export type AddPaymentMethodCode = {
  type: ApplicationActionType.ADD_PAYMENT_METHOD_CODE;
  cart: CartItem[];
  cartId: string;
  paymentMethodCode: PaymentMethodCode | null;
  catalogue?: Catalogue;
  productId?: string;
  acquiringChannel?: AcquiringChannel;
};

export type SetSelectablePaymentMethodCodes = {
  type: ApplicationActionType.SET_SELECTABLE_PAYMENT_CODES;
  catalogue: Catalogue;
};

export type UpdateSelectablePaymentMethodCodes = {
  type: ApplicationActionType.UPDATE_SELECTABLE_PAYMENT_CODES;
  deliveries?: AppDelivery[];
  collections?: Collection[];
  catalogue?: Catalogue;
};

export type UpdateSelectedPaymentMethod = {
  type: ApplicationActionType.UPDATE_SELECTED_PAYMENT_METHOD;
  paymentMethodCode?: PaymentMethodCode;
};

export type ShowDeviceActivation = {
  type: ApplicationActionType.SHOW_DEVICE_ACTIVATION;
  productItems: CatalogueItem[];
};

export type UpdateDeliveryOptions = {
  type: ApplicationActionType.UPDATE_DELIVERY_OPTIONS;
  device: AppDelivery | Collection;
  isDelivery: boolean;
};

export type NormaliseAddresses = {
  type: ApplicationActionType.NORMALISE_ADDRESSES;
  normalise?: boolean;
};

export type ApplicationAction =
  | StartOrderJourney
  | RestartOrderJourney
  | StartTapOnPhoneJourney
  | NavigateToNextStep
  | NavigateToPreviousStep
  | UpdateSolutionType
  | UpdateBasketQuantity
  | RemoveBasketQuantity
  | UpdateCartQuantity
  | RemoveCartQuantity
  | AddDeliveryAddress
  | UpdateDeliveryAddress
  | UpdateContactDetails
  | UpdateReferenceNumber
  | UpdatePaymentType
  | MultipleActions
  | UpdatePaymentStatus
  | UpdateDirectorDetails
  | StartOnboardingJourney
  | UpdateDocuments
  | UpdateBusinessDetails
  | UpdateResidentialAddress
  | UpdateBankingDetails
  | UpdateDeliveries
  | UpdateCreditVettingStatus
  | UpdateCardFees
  | SetWholeApp
  | KillApplication
  | QuickEditAction
  | UpdateSalesDetails
  | UpdateRemotePaymentStatus
  | HighlightAddress
  | HighlightDelivery
  | ResetRemotePaymentStatus
  | SetGatewayPaymentStatus
  | UpdateGatewayPaymentStatus
  | ResetGatewayPaymentStatus
  | AttachSerialNumber
  | UpdateCardMachine
  | DeviceActivation
  | Update3rdPartyDefaultAddressSolutions
  | InitPaymentMethodCode
  | AddPaymentMethodCode
  | FeesSlider
  | SetSelectablePaymentMethodCodes
  | UpdateSelectablePaymentMethodCodes
  | UpdateSelectedPaymentMethod
  | ShowDeviceActivation
  | UpdateDeviceDeliveries
  | AddAddress
  | RemoveAddress
  | UpdateAddress
  | UpdateDeviceCollections
  | SetDPOJourney
  | UpdateCartPaymentType
  | UpdateDeliveryOptions
  | SetMerchantAgreement
  | SetSignedMerchantAgreement
  | SetMerchantAgreementUploaded
  | SetTermsAndConditions
  | SetContinueWithOTPApp
  | NormaliseAddresses;
